import Button from "components/common/Button";
import CustomToast from "components/common/CustomToast";
import ModalContainer, { ModalContainerProps } from "components/common/ModalContainer";
import { useRemoveSpaceGuest, useRemoveSpaceMember } from "hooks/spaceHooks";
import toast from "react-hot-toast";
import { Space } from "redux/Ldb/types/spaceTypes";

type SpaceDetailsRemoveMembersModalProps = {
    spaceData: Space;
    member: { fullName: string; id: string; isGuest: boolean };
} & Omit<ModalContainerProps, "children">;

const SpaceDetailsRemoveMembersModal = (props: SpaceDetailsRemoveMembersModalProps) => {
    const { spaceData, member, closeModal, ...rest } = props;

    const [removeMemberMutation, removeMemberMutationResult] = useRemoveSpaceMember();
    const [removeGuestMutation, removeGuestMutationResult] = useRemoveSpaceGuest();

    async function handleRemoveMember(memberId: string) {
        try {
            await removeMemberMutation(spaceData?._id, memberId);
            toast.custom((t) => (
                <CustomToast t={t} message="Member removed successfully" type="success" />
            ));
        } catch (error) {
            toast.custom((t) => <CustomToast t={t} message={error?.message} type="error" />);
        }
    }

    async function handleRemoveGuest(guestId: string) {
        try {
            await removeGuestMutation(spaceData?._id, guestId);
            toast.custom((t) => (
                <CustomToast t={t} message="Guest removed successfully" type="success" />
            ));
        } catch (error) {
            toast.custom((t) => <CustomToast t={t} message={error?.message} type="error" />);
        }
    }

    return (
        <ModalContainer
            showCloseIcon={false}
            tailwindClassName="w-full max-w-[500px] max-h-[90vh] bg-white rounded-lg p-[16px]"
            closeModal={() => {
                closeModal?.();
            }}
            {...rest}
        >
            <div>
                <div className="flex justify-between items-center">
                    <p className="text-lg font-rocGroteskMedium text-g-75">
                        {member?.isGuest ? "Remove guest from space" : "Remove team member"}
                    </p>
                    <i
                        className="ri-close-fill text-xl text-gm-50 cursor-pointer"
                        onClick={() => {
                            closeModal?.();
                        }}
                    ></i>
                </div>

                <div className="pt-1 max-h-[66vh] overflow-y-auto">
                    <p className="text-sm font-rocGroteskMedium text-slate-500">
                        {member?.isGuest
                            ? `Are you sure you want to remove ${member?.fullName} from this space? They will lose access immediately.`
                            : `Are you sure you want to remove ${member?.fullName} from this space? Once
                        removed, they will no longer be able to view or access its content.`}
                    </p>

                    <div className="flex items-center justify-end gap-2 mt-[24px]">
                        <Button
                            btnText="No, cancel"
                            onClick={() => {
                                closeModal?.();
                            }}
                            btnClassname="!w-fit whitespace-nowrap !py-[8px] !px-[12px] !bg-slate-100 !text-g-75"
                            disabled={
                                removeMemberMutationResult.isLoading ||
                                removeGuestMutationResult.isLoading
                            }
                        />
                        <Button
                            btnText="Remove member"
                            onClick={
                                member?.isGuest
                                    ? () => handleRemoveGuest(member?.id)
                                    : () => handleRemoveMember(member?.id)
                            }
                            isLoading={
                                removeMemberMutationResult.isLoading ||
                                removeGuestMutationResult.isLoading
                            }
                            btnClassname="!w-fit whitespace-nowrap !py-[8px] !px-[12px] !bg-[#E4281D] !text-white"
                        />
                    </div>
                </div>
            </div>
        </ModalContainer>
    );
};

export default SpaceDetailsRemoveMembersModal;
